import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Ranger (manuale V1, rr. 5203-5258).
 *
 * - **3 mosse automatiche** (`type: 'starting'`): *Seguire Tracce*,
 *   *Ramingo*, *Un Posto Sicuro*.
 * - **2 mosse di scelta** (`pickCount: 1`): *Marchio del Cacciatore*,
 *   *Compagno Animale*. Manuale r. 5222: "*Inoltre, scegli una delle
 *   seguenti mosse*".
 *
 * **Pattern critico — *Compagno Animale* description-only (D2 della PR)**:
 * il sistema dei Seguaci a Ranghi 1-3 (Gregari/Evocazioni/Compagno Animale)
 * è **rinviato a una PR dedicata post-12 classi** (FI-8 P1, decisione
 * 2026-05-10). Le sotto-scelte del Compagno (specie, modificatore +2,
 * capacità utili) restano in description; le 4 mosse advanced che lo
 * richiedono (*Ben Addestrato*, *Il Miglior Amico dell'Uomo*, *Alleato
 * Innaturale*, *Abilità Speciale*) hanno comunque `prerequisites:
 * ['ranger.compagno-animale']` strutturato — quando arriverà la PR FI-8,
 * la logica builder sarà già pronta.
 *
 * **Pattern *Ramingo* trigger archetipo Elfo (D6)**: la mossa cita
 * direttamente «Se hai l'archetipo «Elfo» del Ranger, trasforma il peggiore
 * dei due risultati in un 10+». Riferimento cross-archetipo nel testo,
 * description-only.
 */

const seguireTracce: Move = {
  id: 'ranger.seguire-tracce',
  name: 'Seguire Tracce',
  type: 'starting',
  classId: 'ranger',
  trigger: 'Quando segui la traccia lasciata da una creatura',
  description:
    "Tira+**SAG**. Con 7-9, riesci a seguire le sue tracce fino a che non si confondono a causa del terreno, del clima o di un cambio nel suo modo di muoversi. Con 10+, lo stesso, ma scegli anche una delle seguenti:\n\n- ottieni un'informazione utile sulla tua preda: il GM ti dirà cosa;\n- determini cosa ha causato l'interrompersi della traccia.",
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Segui le tracce + scegli 1 opzione dalla lista.' },
    on7to9: { text: 'Segui le tracce finché non si confondono.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ramingo: Move = {
  id: 'ranger.ramingo',
  name: 'Ramingo',
  type: 'starting',
  classId: 'ranger',
  trigger: 'Quando *intraprendi un viaggio pericoloso*',
  description:
    "Puoi ricoprire due ruoli. Fai un tiro separato per ciascun ruolo scelto. Se hai l'archetipo «*Elfo*» del Ranger, trasforma il peggiore dei due risultati in un 10+.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const unPostoSicuro: Move = {
  id: 'ranger.un-posto-sicuro',
  name: 'Un Posto Sicuro',
  type: 'starting',
  classId: 'ranger',
  trigger: 'Quando stabilisci i turni di guardia per la notte',
  description:
    'Tutti prendono +1 a *fare la guardia*. Inoltre, se dovesse accadere qualcosa, ogni membro del gruppo prende +1 al prossimo tiro.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const marchioDelCacciatore: Move = {
  id: 'ranger.marchio-del-cacciatore',
  name: 'Marchio del Cacciatore',
  type: 'starting',
  classId: 'ranger',
  trigger:
    'Quando studi per un tempo sufficiente una creatura che puoi vedere o della quale stai *Seguendo le Tracce*',
  description:
    "Puoi decidere di marchiarla, e finché il marchio permane le infliggi +2 danni. Puoi rimuovere il marchio per ottenere uno dei seguenti effetti, purché tu sia in grado di vedere la preda:\n\n- crei un vantaggio nei suoi confronti: prendi +1 al prossimo tiro quando lo sfrutti;\n- trasformi un tuo tiro di *sfidare il pericolo* contro di essa in un 10+;\n- trovi il modo di piombarle addosso prima che essa possa reagire.\n\nPuoi usare *Marchio del Cacciatore* su una sola creatura per volta. Se ne marchi un'altra, il marchio sulla prima viene meno senza attivare gli effetti della rimozione.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const compagnoAnimale: Move = {
  id: 'ranger.compagno-animale',
  name: 'Compagno Animale',
  type: 'starting',
  classId: 'ranger',
  trigger: 'Alla creazione del personaggio (configurazione del Compagno)',
  description:
    'Hai un legame sovrannaturale con un animale che ti è fedele. Non condividete un linguaggio comune, ma potete contare su una basilare forma di comunicazione (niente concetti complessi). Esso agisce sempre nel modo che desideri, senza dover *dare ordini a un gregario*.\n\nQuando **collabori con il tuo *Compagno Animale* per fare qualcosa**, descrivi in che modo lo fate: se è plausibile che il suo aiuto ti avvantaggi, prendi +1 al tiro.\n\nAlcune mosse avanzate ti consentono di dare ulteriori capacità utili al tuo Compagno Animale. Puoi scegliere anche più volte la stessa opzione dalla lista, ma al massimo il tuo alleato può avere il d10 come dado di Danno, +2 Armatura e +6 Carico.\n\nSe per qualsiasi ragione il tuo *Compagno Animale* dovesse morire o non essere più disponibile, puoi instaurare un nuovo legame con un altro animale. Il GM ti dirà quando troverai il compagno adatto.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-8a: scelte strutturate del Compagno Animale (specie / modificatore /
  // capacità) — alimentano il `FollowerTemplate` `ranger-compagno-animale`.
  choiceGroups: [
    {
      id: 'specie',
      label: 'Specie',
      description:
        'Scegli la specie del tuo Compagno Animale (o "Altro" per concordarne una con il GM).',
      pickCount: 1,
      options: [
        { id: 'lupo', label: 'Lupo' },
        { id: 'puma', label: 'Puma' },
        { id: 'orso', label: 'Orso' },
        { id: 'aquila', label: 'Aquila' },
        { id: 'cane', label: 'Cane' },
        { id: 'falco', label: 'Falco' },
        { id: 'gatto', label: 'Gatto' },
        { id: 'gufo', label: 'Gufo' },
        { id: 'piccione', label: 'Piccione' },
        { id: 'ratto', label: 'Ratto' },
        { id: 'mulo', label: 'Mulo' },
        { id: 'altro', label: 'Altro (concordata con il GM)' },
      ],
    },
    {
      id: 'modificatore',
      label: 'Modificatore di Caratteristica a +2',
      description: 'Una Caratteristica del tuo Compagno parte a +2 invece di +1.',
      pickCount: 1,
      options: [
        { id: 'forza', label: 'Forza' },
        { id: 'destrezza', label: 'Destrezza' },
        { id: 'costituzione', label: 'Costituzione' },
        { id: 'saggezza', label: 'Saggezza' },
      ],
    },
    {
      id: 'capacita',
      label: 'Capacità utile iniziale',
      description:
        'Scegli una capacità che caratterizza il tuo Compagno (o "Altro" per concordarne una con il GM).',
      pickCount: 1,
      options: [
        { id: 'feroce', label: 'Feroce (dado di Danno +1 categoria, max d10)' },
        { id: 'tenace', label: 'Tenace (+1 Armatura, max +2)' },
        { id: 'resistente', label: 'Resistente (+3 Carico, max +6)' },
        { id: 'altro', label: 'Altro (concordata con il GM)' },
      ],
    },
  ],
  narrativeFields: [
    {
      id: 'nome-compagno',
      label: 'Nome del Compagno Animale',
      placeholder: 'es. Zanna, Mira, Solo, Krakuk, Volo, Ombra…',
      required: true,
      multiline: false,
    },
    {
      id: 'specie-custom',
      label: 'Specie personalizzata',
      placeholder: 'es. drago di palude, grifone delle nevi…',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'specie', optionId: 'altro' },
    },
    {
      id: 'capacita-custom-label',
      label: 'Capacità personalizzata: nome',
      placeholder: 'es. Sensi acuti, Inseguitore implacabile…',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'capacita', optionId: 'altro' },
    },
    {
      id: 'capacita-custom-description',
      label: 'Capacità personalizzata: descrizione (opzionale)',
      placeholder: 'es. Riconosce odori che gli umani non percepiscono…',
      required: false,
      multiline: true,
      requiresChoice: { groupId: 'capacita', optionId: 'altro' },
    },
  ],
  followerTemplateId: 'ranger-compagno-animale',
};

/**
 * Mosse iniziali AUTOMATICHE (3): prese tutte al livello 1 senza scelta.
 */
export const rangerStartingAutoMoves: readonly Move[] = [
  seguireTracce,
  ramingo,
  unPostoSicuro,
] as const;

/**
 * Mosse iniziali di SCELTA (2): il giocatore ne sceglie UNA al livello 1
 * (`pickCount: 1`). *Marchio del Cacciatore* o *Compagno Animale*.
 */
export const rangerStartingChoiceMoves: readonly Move[] = [
  marchioDelCacciatore,
  compagnoAnimale,
] as const;

/** Tutte le mosse iniziali (auto + scelta). */
export const rangerInitialMoves: readonly Move[] = [
  ...rangerStartingAutoMoves,
  ...rangerStartingChoiceMoves,
];
